/**
 * Exact data model ported from the CPC Government Checklist standalone HTML
 * mockup (National Overview / Checklist Explorer / Indicator Detail / Review
 * Progress screens). Field names, values, and all user-facing strings are
 * reproduced verbatim from the source prototype — do not reword.
 */

export const GREEN = '#5c9469';
export const GOLD = '#c99a3f';
export const RUST = '#c0553a';
export const TEAL = '#3f7a94'; // Stakeholders 2–6 — Government Checklist

// Rubric scale (score tier, indicator/item status, data confidence) — best to worst.
export const EMERALD = '#03AF9A';
export const PURPLE = '#0096c8'; // also Stakeholder/Target 1 — Children's Assessment (cpc.gcforum.org brand palette)
export const AMBER = '#EBB941';
export const CORAL = '#F05E63';

/**
 * Solid, pre-blended equivalents of the old rgba(...,.14) overlay tints
 * (each computed against a white background — e.g. EMERALD_BG was
 * rgba(3,175,154,.14), which is #DCF4F1 once flattened to opaque).
 *
 * They have to be opaque: a semi-transparent tint blends with whatever sits
 * behind it, so the exact same rgba value read as a pale mint on a white
 * light-mode card and a muddy, darker green on a dark-mode card — the ONE
 * thing status/rubric colors must never do is look different by theme.
 */
export const EMERALD_BG = '#DCF4F1';
export const PURPLE_BG = '#EEEAF9';
export const AMBER_BG = '#FCF5E4';
export const CORAL_BG = '#FDE8E9';

export const STAKEHOLDERS = [
    {id: 1, name: "Competent Children", color: PURPLE, score: 63, indicators: 15, met: 5, partial: 9, notMet: 1, toolkit: "Children's Assessment Toolkit"},
    {id: 2, name: 'Supportive Families & Communities', color: TEAL, score: 59, indicators: 17, met: 6, partial: 10, notMet: 1, toolkit: 'Government Checklist Toolkit'},
    {id: 3, name: 'Safe Schools', color: TEAL, score: 55, indicators: 18, met: 5, partial: 11, notMet: 2, toolkit: 'Government Checklist Toolkit'},
    {id: 4, name: 'Responsible Companies', color: TEAL, score: 63, indicators: 19, met: 8, partial: 10, notMet: 1, toolkit: 'Government Checklist Toolkit'},
    {id: 5, name: 'Secure Technology Infrastructure', color: TEAL, score: 59, indicators: 16, met: 7, partial: 8, notMet: 1, toolkit: 'Government Checklist Toolkit'},
    {id: 6, name: 'Resilient Government', color: TEAL, score: 55, indicators: 15, met: 7, partial: 6, notMet: 2, toolkit: 'Government Checklist Toolkit'},
];

export const TARGETS = [
    {n: 1, s: 1, name: "Children's Cybersecurity and Privacy Management", score: 52, met: 2, partial: 3, notMet: 0},
    {n: 2, s: 1, name: "Children's Cyber Safety Management", score: 58, met: 2, partial: 3, notMet: 0},
    {n: 3, s: 1, name: "Children's Cyber Civility", score: 51, met: 1, partial: 3, notMet: 1},
    {n: 4, s: 2, name: 'Family Technology Access', score: 64, met: 3, partial: 3, notMet: 0},
    {n: 5, s: 2, name: 'Cyber Parenting and Family Capacity', score: 49, met: 1, partial: 4, notMet: 1},
    {n: 6, s: 2, name: 'Public Cyber Skills and Awareness', score: 53, met: 2, partial: 3, notMet: 0},
    {n: 7, s: 3, name: 'Cyber Citizenship Education and Teacher Readiness', score: 56, met: 2, partial: 4, notMet: 0},
    {n: 8, s: 3, name: 'School Online Safety Governance and Response', score: 50, met: 1, partial: 4, notMet: 1},
    {n: 9, s: 3, name: 'School Access to Safe and Secure Technology', score: 55, met: 2, partial: 3, notMet: 1},
    {n: 10, s: 4, name: 'Government-Mandated Safety-by-Design Requirements', score: 54, met: 2, partial: 4, notMet: 0},
    {n: 11, s: 4, name: 'Government-Mandated Platform Accountability and Redress', score: 61, met: 4, partial: 3, notMet: 0},
    {n: 12, s: 4, name: 'Industry Transparency and Public Trust', score: 57, met: 2, partial: 3, notMet: 1},
    {n: 13, s: 5, name: 'Universal and Meaningful Connectivity', score: 66, met: 3, partial: 2, notMet: 0},
    {n: 14, s: 5, name: 'Inclusive Online Services and Content', score: 58, met: 1, partial: 3, notMet: 1},
    {n: 15, s: 5, name: 'National Cybersecurity Readiness', score: 72, met: 3, partial: 3, notMet: 0},
    {n: 16, s: 6, name: 'Child Online Safety Laws and Policies', score: 68, met: 3, partial: 2, notMet: 0},
    {n: 17, s: 6, name: 'National Strategy, Oversight, and Monitoring', score: 62, met: 2, partial: 2, notMet: 1},
    {n: 18, s: 6, name: 'National Response, Enforcement, and Care', score: 59, met: 2, partial: 2, notMet: 1},
];

/**
 * Item shape mirrors the source spreadsheet's Final_Item_Scores tab:
 *   src: "Internal" | "ExternalSurvey" | "ExternalFixed"
 *   dataFrom: "internal" | "external" | "missing" (which source supplied the value)
 *   raw/outOf: final_raw_score / denominator, rationale: citation_claims,
 *   sourceDetails: source_details_title_issuer_date_location_level, urls: citation_urls,
 *   extTitle/extOrg: Publication or Dataset Title + Organization, year: Data Year,
 *   region: Regional Reference Group, q: Final Checklist Survey Question, opts: response options
 *
 * These 10 indicators are the sample set from the source prototype (representing
 * every distinct data pattern the full 85-indicator dataset contains).
 */
export const INDICATORS = [
    {
        code: 'FC-05', t: 5, s: 2, name: 'Parental Guidance Preventing Child Sexual Abuse', status: 'No', score: 33, conf: 'High',
        question: 'Has the government or a government-affiliated organisation produced guidance for parents on strategies to prevent child sexual abuse?',
        items: [{
            code: 'Cp251', name: 'Parental Guidance Preventing CSEA', src: 'ExternalSurvey', dataFrom: 'external', conf: 'High', raw: 0, outOf: 1, cur: 1,
            q: 'Has the government or a government-affiliated organization produced guidance for parents on strategies to prevent child sexual abuse?',
            opts: ['No, or no publicly available information was identified.', 'Yes.'],
            extTitle: 'Out of the Shadows Index 2022', extOrg: 'Economist Impact',
        }],
    },
    {
        code: 'FC-06', t: 6, s: 2, name: 'Public Online-Safety Awareness Campaigns', status: 'Partial', score: 54, conf: 'Moderate',
        question: 'To what extent does the government run sustained public awareness campaigns on online safety?',
        items: [{
            code: 'cp261', name: 'Sustained National Public Awareness Activity', src: 'Internal', dataFrom: 'internal', conf: 'Moderate', raw: 2, outOf: 5, cur: 2,
            q: 'To what extent are online-safety public awareness activities sustained and resourced at national scale?',
            opts: [
                'No national public awareness activity.',
                'One-off or campaign-led activity with no continuity.',
                'Recurring activity in some regions or sectors, partially resourced.',
                'National, multi-year programme with dedicated resourcing and reach targets.',
                'Institutionalised national programme with sustained funding and measured population reach.',
            ],
            rationale: "National online-safety awareness activity is delivered through recurring campaign cycles led by the national regulator, with materials distributed to schools and public libraries. Delivery is documented in the regulator's annual report, but coverage remains concentrated in metropolitan regions and funding is allocated annually rather than through a multi-year commitment. No population-level reach target or independent evaluation of campaign effect was identified. On this basis the assessment confirms recurring but partially resourced activity, consistent with level 2 rather than a national multi-year programme.",
            sourceDetails: 'National Online Safety Regulator, Annual Report 2025, published 14 March 2026, Chapter 4 "Public Awareness and Education", pages 38–44, national level.',
            urls: ['https://example.gov/regulator/annual-report-2025.pdf'],
        }],
    },
    {
        code: 'SS-09', t: 9, s: 3, name: 'School Access to Safe and Secure Technology', status: 'Yes', score: 71, conf: 'High',
        question: 'To what extent do schools have access to filtered, secure and age-appropriate technology?',
        items: [{
            code: 'Cp291', name: 'Lower-Secondary School Internet Access for Pedagogical Purposes', src: 'ExternalFixed', dataFrom: 'external', conf: 'High', raw: 81.4, outOf: 100,
            measures: 'Proportion of lower-secondary schools with access to the internet for pedagogical purposes, by education level (%).',
            extTitle: 'UIS Education Statistics — ICT in Education', extOrg: 'UNESCO Institute for Statistics', year: '2024',
        }],
    },
    {
        code: 'RC-10', t: 10, s: 4, name: 'Government-Mandated Safety-by-Design Requirements', status: 'Partial', score: 62, conf: 'High',
        question: 'To what extent does the government mandate safety-by-design requirements for online services likely to be accessed by children?',
        items: [
            {
                code: 'cp301', name: 'Statutory Safety-by-Design Duty', src: 'Internal', dataFrom: 'internal', conf: 'High', raw: 3, outOf: 5, cur: 3,
                q: 'To what extent are safety-by-design requirements for child-accessed services established in binding law?',
                opts: [
                    'No statutory safety-by-design requirement.',
                    'Voluntary codes or principles only.',
                    'Statutory duty exists but is narrow in scope or not yet fully in force.',
                    'Statutory duty in force with defined obligations, enforcement developing.',
                    'Comprehensive statutory duty in force, enforced with published outcomes.',
                ],
                rationale: 'A statutory duty of care for services likely to be accessed by children is established in primary legislation and has entered into force for the largest service categories. The duty defines risk-assessment and mitigation obligations and confers enforcement powers on the national regulator. However, secondary regulations extending the duty to smaller and intermediary services remain in draft, and the regulator has not yet published enforcement outcomes. The assessment therefore confirms a statutory duty that is in force but narrower in scope than the full legislative intent, corresponding to level 3.',
                sourceDetails: 'Parliament, Online Safety Act 2024 (as amended 2025), Part 3, sections 11–19; commencement order of 2 January 2026, national level.',
                urls: ['https://example.gov/legislation/online-safety-act-2024', 'https://example.gov/regulator/commencement-order-2026.pdf'],
            },
            {
                code: 'cp302', name: 'Proportionate Age-Assurance Expectations', src: 'Internal', dataFrom: 'missing', conf: 'Low', raw: 2, outOf: 5, cur: 2,
                q: 'To what extent does the government set expectations for proportionate age assurance on high-risk services?',
                opts: [
                    'No expectations set.',
                    'General statements without operational detail.',
                    'Expectations set for some services, guidance emerging.',
                    'Clear expectations with proportionality and privacy safeguards, partial rollout.',
                    'Established expectations with audited compliance and privacy safeguards.',
                ],
                region: 'Western Europe — high-income regional reference group',
            },
        ],
    },
    {
        code: 'RC-12', t: 12, s: 4, name: 'Industry Transparency and Public Trust', status: 'No', score: 38, conf: 'Moderate',
        question: 'To what extent are online service providers required to publish transparency reporting relevant to child safety?',
        items: [{
            code: 'Cp321', name: 'Mandated Child-Safety Transparency Reporting', src: 'ExternalSurvey', dataFrom: 'internal', conf: 'Moderate', raw: 1, outOf: 1, cur: 2,
            q: 'Does the government require online service providers to publish transparency reporting on measures taken to protect children?',
            opts: ['No, or no publicly available information was identified.', 'Yes.'],
            extTitle: 'Out of the Shadows Index 2022', extOrg: 'Economist Impact',
            rationale: 'The external source recorded no transparency-reporting requirement at the time of its 2022 data collection. Since then, the Online Safety Act 2024 has introduced a statutory transparency-reporting duty, and the national regulator issued the first transparency notices to categorised services in 2025. Published notices confirm that reporting is now mandatory for designated providers, although the first reporting cycle is incomplete. The assessment therefore updates the external result to reflect the current legal position.',
            sourceDetails: 'National Online Safety Regulator, First Transparency Notices Register 2025, published 30 September 2025, Table 2 "Categorised services issued notices", national level.',
            urls: ['https://example.gov/regulator/transparency-notices-register-2025'],
        }],
    },
    {
        code: 'TI-14', t: 14, s: 5, name: 'Inclusive Online Services and Content', status: 'Partial', score: 47, conf: 'Low',
        question: 'To what extent are online public services and content accessible and inclusive for all children?',
        items: [{
            code: 'Cp341', name: 'Accessibility Compliance of Government Digital Services', src: 'ExternalFixed', dataFrom: 'missing', conf: 'Low', raw: 44, outOf: 100,
            measures: 'Proportion of government digital services meeting recognised web accessibility standards (%).',
            extTitle: 'E-Government Development Index 2024', extOrg: 'United Nations Department of Economic and Social Affairs',
            region: 'Western Europe — high-income regional reference group',
        }],
    },
    {
        code: 'TI-15', t: 15, s: 5, name: 'National Cybersecurity Readiness', status: 'Yes', score: 74, conf: 'High',
        question: 'To what extent is the country prepared to prevent, detect and respond to cyber threats affecting children and the public?',
        items: [{
            code: 'Cp351', name: 'National Cybersecurity Commitment', src: 'ExternalFixed', dataFrom: 'external', conf: 'High', raw: 74.2, outOf: 100,
            measures: 'Overall national commitment to cybersecurity across legal, technical, organisational, capacity-development and cooperative measures (index score, 0–100).',
            extTitle: 'Global Cybersecurity Index 2024', extOrg: 'International Telecommunication Union', year: '2024',
        }],
    },
    {
        code: 'RG-16', t: 16, s: 6, name: 'Child Online Safety Laws and Policies', status: 'Partial', score: 64, conf: 'High',
        question: "To what extent does national law comprehensively address children's online safety?",
        items: [{
            code: 'cp361', name: 'Comprehensive Child Online Safety Legal Framework', src: 'Internal', dataFrom: 'internal', conf: 'High', raw: 3, outOf: 5, cur: 3,
            q: 'To what extent does national legislation comprehensively address online risks to children?',
            opts: [
                'No dedicated child online-safety legislation.',
                'Fragmented provisions across unrelated statutes.',
                'Dedicated framework covering major risks, gaps remain.',
                'Comprehensive framework in force, secondary legislation developing.',
                'Comprehensive, regularly updated framework in force with full implementing regulations.',
            ],
            rationale: "A dedicated legislative framework addresses illegal content, child sexual abuse material, harmful design features, and complaint handling, and is supported by a statutory code of practice for services likely to be accessed by children. Provisions on age assurance and on private messaging services remain subject to unimplemented secondary regulation, and no consolidated review cycle is established in law. The assessment confirms a dedicated framework covering the major risk categories with identifiable gaps, corresponding to level 3.",
            sourceDetails: 'Parliament, Online Safety Act 2024 (consolidated text, as amended 2025), Parts 2–5; National Online Safety Regulator, Children\'s Code of Practice, effective 25 July 2025, national level.',
            urls: ['https://example.gov/legislation/online-safety-act-2024', 'https://example.gov/regulator/childrens-code-of-practice-2025.pdf'],
        }],
    },
    {
        code: 'RG-17', t: 17, s: 6, name: 'National Strategy, Oversight and Monitoring', status: 'Partial', score: 51, conf: 'High',
        question: 'To what extent are digital literacy and online safety training integrated into national strategies or policies?',
        items: [{
            code: 'cp254', name: 'National Cyber Safety Training Strategy', src: 'Internal', dataFrom: 'internal', conf: 'High', raw: 2, outOf: 5, cur: 2,
            q: 'To what extent are digital literacy and online safety training integrated into national strategies or policies?',
            opts: [
                'Digital literacy and online safety training are not referenced in national strategies or policies.',
                'They are referenced in isolated documents, but with no clear ownership, follow-through, implementation plan, or resourcing.',
                'They are included in one or more national strategies or policies, but only partially operationalized, with limited resourcing, unclear delivery responsibilities, or incomplete implementation plans.',
                'They are integrated across multiple national strategies (education, ICT, workforce or public safety) with some measurable targets, but binding cross-ministerial mandates, sustained funding, delivery accountability, or monitoring remain incomplete.',
                'They are fully institutionalized in long-term national development plans, with binding cross-ministerial mandates, sustained funding, measurable targets, delivery accountability, and monitoring mechanisms.',
            ],
            rationale: 'Digital literacy and online safety training are referenced in the national digital strategy and in a ministerial education policy statement, but neither document assigns a lead delivery body, sets a resourced implementation plan, nor establishes reporting obligations. No budget line dedicated to online safety training was identified in the published national budget, and no monitoring indicators are attached to the commitments. The assessment therefore confirms references in isolated documents without ownership or resourcing, corresponding to level 2.',
            sourceDetails: 'Ministry of Digital Affairs, National Digital Strategy 2024–2030, published 8 May 2024, section 5.3 "Digital Skills for All", pages 61–63; Ministry of Education, Policy Statement on Digital Citizenship, 19 November 2024, paragraph 12, national level.',
            urls: ['https://example.gov/digital/national-digital-strategy-2024-2030.pdf', 'https://example.gov/education/digital-citizenship-statement-2024'],
        }],
    },
    {
        code: 'RG-18', t: 18, s: 6, name: 'National Response, Enforcement and Care', status: 'No', score: 44, conf: 'Low',
        question: 'To what extent are national reporting, enforcement and victim-care systems established for online harms to children?',
        items: [{
            code: 'Cp381', name: 'Integrated Reporting and Victim-Care Pathway', src: 'ExternalSurvey', dataFrom: 'missing', conf: 'Low', raw: 0, outOf: 1, cur: 1,
            q: 'Does the country operate a national reporting mechanism linked to a defined victim-care pathway for online child harm?',
            opts: ['No, or no publicly available information was identified.', 'Yes.'],
            extTitle: 'Out of the Shadows Index 2022', extOrg: 'Economist Impact',
            region: 'Western Europe — high-income regional reference group',
        }],
    },
];

export const STATUS_MEANING = {
    Yes: 'All indicator requirements are satisfied.',
    Partial: 'Some requirements are satisfied, but important gaps remain.',
    No: 'The indicator requirements are not satisfied.',
};

export const statusLabel = (st) => (st === 'Yes' ? 'Met' : st === 'Partial' ? 'Partially' : 'Not Met');

export const statusColors = (st) => (
    st === 'Yes' ? {bg: EMERALD_BG, fg: EMERALD}
        : st === 'Partial' ? {bg: AMBER_BG, fg: AMBER}
            : {bg: CORAL_BG, fg: CORAL}
);

export const scoreColor = (v) => (v >= 70 ? GREEN : v >= 45 ? GOLD : RUST);

/** Confidence band colors — keyed by the 'High' | 'Moderate' | 'Low' | 'Insufficient Evidence' value. */
export const CONFIDENCE_COLORS = {
    High: {fg: EMERALD, bg: EMERALD_BG},
    Moderate: {fg: PURPLE, bg: PURPLE_BG},
    Low: {fg: AMBER, bg: AMBER_BG},
    'Insufficient Evidence': {fg: CORAL, bg: CORAL_BG},
};

export const confidenceColors = (conf) => CONFIDENCE_COLORS[conf] || CONFIDENCE_COLORS.Low;

/** Score tier band — returns {label, fg, bg, pct, meaning}. */
export const tier = (v) => {
    if (v >= 75) return {label: 'Tier 1 · Leading', fg: EMERALD, bg: EMERALD_BG, pct: '75th–100th percentile', meaning: 'Performance is among the highest in the global reference group.'};
    if (v >= 50) return {label: 'Tier 2 · Advancing', fg: PURPLE, bg: PURPLE_BG, pct: '50th–75th percentile', meaning: 'Performance is above the global midpoint and progressing well.'};
    if (v >= 25) return {label: 'Tier 3 · Developing', fg: AMBER, bg: AMBER_BG, pct: '25th–50th percentile', meaning: 'Some measures are in place, but important gaps remain.'};
    return {label: 'Tier 4 · Emerging', fg: CORAL, bg: CORAL_BG, pct: '0–25th percentile', meaning: 'Systems or practices are at an early stage of development.'};
};

/** CSS conic-gradient string for a circular progress ring. */
export const ring = (pct, color) => `conic-gradient(${color} ${Math.round(pct * 3.6)}deg, var(--cpc-surface-subtle, #e6e6dd) 0deg)`;

export const TOTAL_IND = 85;
export const TOTAL_ITEMS = 242;
/**
 * Tier styling looked up by the tier LABEL rather than derived from a score.
 *
 * The CPC side stores `maturity` as "Tier 1".."Tier 4" already computed, and
 * their schema note says plainly: precomputed server-side, do not recompute
 * client-side. There is also no numeric score to recompute from — Seong Min,
 * 5 Aug: "we absolutely do not display scores or rankings".
 *
 * So real data goes through here; only the offline sample still uses tier(score).
 */
export const tierByLabel = (maturity) => {
    const n = parseInt(String(maturity || '').match(/(\d+)/)?.[1], 10);
    if (n === 1) return tier(80);
    if (n === 2) return tier(60);
    if (n === 3) return tier(30);
    if (n === 4) return tier(10);
    // Unknown or missing: show the raw value rather than silently claiming a tier.
    return {label: String(maturity || '—'), fg: '#5b5b54', bg: '#eeeeea', pct: '', meaning: ''};
};

export const NATIONAL_SCORE = 61;

export const BREAKDOWN = [
    {label: 'Met', color: EMERALD, n: 38, meaning: STATUS_MEANING.Yes},
    {label: 'Partially', color: AMBER, n: 54, meaning: STATUS_MEANING.Partial},
    {label: 'Not Met', color: CORAL, n: 8, meaning: STATUS_MEANING.No},
];

export const VALIDATION_CRITERIA = [
    'alignment with the applicable rubric',
    'relevance and authority of the evidence',
    'publication and effective dates',
    'implementation and enforcement status',
    'source quality and traceability',
    'consistency with the CPC Index methodology',
    'any remaining limitations or evidence gaps',
];

export const VALIDATION_OUTCOMES = [
    {label: 'Accept', color: GREEN},
    {label: 'Modify', color: '#7fa87f'},
    {label: 'Decline', color: RUST},
];

export const VALIDATION_NOTICE_SHORT =
    'Submitting a revision proposal does not automatically change the CPC Index score. The CPCI team will validate each proposed revision and may accept, modify, return for clarification or decline the proposal.';

export const VALIDATION_NOTICE_LONG =
    'Submitting a revision proposal does not automatically change the CPC Index score. The CPC Index review team will assess alignment with the applicable rubric, relevance and authority of the evidence, jurisdictional and population coverage, publication and effective dates, implementation and enforcement status, source quality and traceability, consistency with the CPC Index methodology, and any remaining limitations or evidence gaps.';

export const TRACE_REQS = [
    {h: 'For documents:', b: 'identify the exact page, article, section, clause, paragraph, table, figure, or annex.'},
    {h: 'For webpages:', b: 'provide the issuing authority, page title, publication or last-update date, URL, and relevant webpage heading.'},
    {h: 'For datasets:', b: 'provide the dataset title, issuing organization, indicator name or code, country, reference year, reported value and unit, applicable population or coverage, and URL or dataset location.'},
];

export const READINESS = [
    {key: 'notStarted', label: 'Not Started', meaning: 'No indicators or assessment items have been reviewed.', fg: '#7a7a72', dot: '#c9c9c0'},
    {key: 'inProgress', label: 'In Progress', meaning: 'The review has begun, but one or more proposed revisions are incomplete.', fg: '#8a6a1a', dot: GOLD},
    {key: 'internal', label: 'Ready for Internal Approval', meaning: 'All proposed revisions include the required rationale and evidence.', fg: '#2f5f75', dot: '#3f7a94'},
    {key: 'cpci', label: 'Ready for CPCI Validation', meaning: 'The authorized approver has confirmed the submission.', fg: '#3f7350', dot: GREEN},
];

export const LEGAL_STATUS = [
    'Not applicable',
    'Proposed or publicly announced',
    'Adopted or approved, but not yet in force',
    'In force or formally effective',
    'Superseded, repealed, or no longer effective',
    'Status not publicly verifiable',
];

export const IMPL_STATUS = [
    'Not applicable',
    'Not yet implemented',
    'Pilot or testing stage only',
    'Partially implemented or limited in coverage',
    'Fully implemented across the required scope',
    'Implementation status not publicly verifiable',
];

export const REASON_GROUPS = [
    {
        title: "A. The country's status or evidence has changed",
        options: [
            {id: 'newLaw', label: 'New or amended law or policy', desc: 'A new legal or policy instrument has been adopted, amended, entered into force, or replaced.'},
            {id: 'newImpl', label: 'New implementation or enforcement evidence', desc: 'New evidence demonstrates that a law, policy, programme, service, regulatory duty, or operational capability has been implemented or enforced.'},
            {id: 'newStats', label: 'Updated official statistics or administrative data', desc: 'A newer or corrected official value is available from an authoritative national or international source.'},
        ],
    },
    {
        title: 'B. The current assessment requires correction',
        options: [
            {id: 'scope', label: 'Scope or applicability correction', desc: 'The assessment applies the wrong jurisdiction, population, institution, service, provider category, time period, or geographical coverage.'},
            {id: 'scoring', label: 'Scoring or methodological correction', desc: 'The rubric, coding rule, calculation, threshold, or scoring methodology was applied incorrectly.'},
            {id: 'source', label: 'Source or citation correction', desc: 'The cited source is incorrect, outdated, incomplete, inaccessible, or does not support the current finding.'},
        ],
    },
    {
        title: 'C. Other',
        options: [
            {id: 'other', label: 'Other material reason', desc: 'Provide a clear explanation of why the proposed revision does not fall within the categories above.'},
        ],
    },
];

export const EVIDENCE_FIELDS = [
    {k: 'evTitle', label: 'Evidence title', help: 'Enter the full title of the document, dataset, decision, or webpage.', req: true, span: '1/3', ph: 'Full official title'},
    {k: 'evIssuer', label: 'Issuing authority', help: 'Identify the ministry, regulator, agency, legislature, court, national statistics office, or other authoritative body responsible for the evidence.', req: true, span: '1/2', ph: 'Issuing body'},
    {k: 'evDate', label: 'Publication, adoption, or update date', help: 'Provide the date most relevant to the evidence.', req: true, span: '2/3', ph: 'e.g. 14 March 2026'},
    {k: 'evJurisdiction', label: 'Applicable jurisdiction', help: 'Specify whether the evidence applies nationally or only to a particular region, state, province, municipality, institution, population, or service category.', req: true, span: '1/2', ph: 'National / Subnational / Sector-specific'},
    {k: 'evLocation', label: 'Evidence location', help: 'Identify the exact page, article, section, clause, paragraph, table, figure, annex, dataset field, indicator code, or official webpage heading.', req: true, span: '2/3', ph: 'e.g. Part 3, section 14(2), page 27'},
    {k: 'evUrl', label: 'Source URL', help: 'Provide a stable link to the official document, dataset, or webpage wherever available.', req: false, span: '1/3', ph: 'https://'},
    {k: 'evExcerpt', label: 'Supporting excerpt', help: 'Paste only the passage directly relevant to the proposed revision, or provide the relevant table, figure, or dataset-field title. Do not paste an entire document.', req: false, span: '1/3', area: true, ph: 'Relevant passage or table/figure title'},
];

export const PROPOSER_FIELDS = [
    {k: 'pName', label: 'Name', req: true, ph: 'Full name'},
    {k: 'pOrg', label: 'Ministry, agency, or organization', req: true, ph: 'Official body'},
    {k: 'pDept', label: 'Department or unit', req: true, ph: 'Department or unit'},
    {k: 'pRole', label: 'Role or position', req: true, ph: 'Role or position'},
    {k: 'pEmail', label: 'Official email address', req: true, ph: 'name@gov.example'},
];

export const ACCEPTED_EVIDENCE = [
    'legislation or regulations',
    'government policies or strategies',
    'official implementation reports',
    'regulatory decisions or enforcement records',
    'government budgets or administrative records',
    'official statistics or datasets',
    'monitoring and evaluation reports',
    'authoritative government webpages',
];

export const stakeById = (id) => STAKEHOLDERS.find((s) => s.id === id);
export const targetById = (n) => TARGETS.find((t) => t.n === n);

/**
 * Case matrix for how an assessment item's score/rationale/evidence is
 * presented, keyed by (src, dataFrom). Returns {kind, title, body[]} or null
 * when the item needs no special notice (plain Internal, non-missing item).
 */
export const itemNotice = (it) => {
    /**
     * No value published, and no estimate applied.
     *
     * Checked FIRST, ahead of every dataFrom branch. Those branches all say the
     * score "was therefore estimated using the applicable regional reference
     * value" — which is true for an `estimated` item and false for this one.
     * Asserting a regional estimate that never happened misrepresents the
     * country's data to the person being asked to confirm it.
     *
     * CONFIRMED by Seong Min (5:02 PM), who was asked directly whether these
     * should be shown as a regional estimate instead:
     *
     *   "If you are referring to the item-level score, we need to display
     *    'no data available'. This is because in such cases final_score_source
     *    stores a specific level's REGION, rather than necessarily being AI or
     *    EXT. If final_score_source is AI or EXT but there is neither a score
     *    nor an estimated value, that is an error, so please let me know."
     *
     * So this case is correct AND it surfaces bad rows: every item reaching it
     * with an AI/EXT source and no non-score code is a data error on their side.
     *
     * `nonScoreMeaning` carries IE / NA / NV / RR when present. Seong Min has
     * not settled that wording yet, so it is shown as supplied rather than
     * reworded here.
     */
    if (it.noValue) {
        return {
            kind: 'warn',
            title: 'No data available for this item',
            body: [
                it.nonScoreMeaning
                    ? `No value is published for this item (${it.nonScoreMeaning}). No regional estimate has been applied.`
                    : 'No value is published for this item, and no regional estimate has been applied.',
                'Where an official country value is available, select Propose Revision and provide the supporting source.',
            ],
        };
    }

    if (it.src === 'Internal' && it.dataFrom === 'missing') {
        return {
            kind: 'warn',
            title: 'Estimated score — no country-specific evidence identified',
            body: ['No sufficiently reliable public evidence was identified for this item. The current score was therefore estimated using the applicable regional reference value. The confidence level records that this score is based on estimated rather than country-specific evidence.'],
        };
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'external') {
        return {
            kind: 'info',
            title: 'External data item',
            body: ['This item is scored using data from an established external source. To preserve comparability and methodological integrity, the assessment question and response options are presented in their original form.'],
        };
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'internal') {
        return {
            kind: 'info',
            title: 'External data item updated with CPC Index evidence',
            body: [
                'This item originates from an established external source. However, the available external result was either unavailable, incomplete, or no longer current. The score has therefore been updated using more recent verifiable evidence identified through CPC Index research.',
                'To preserve comparability with the original source, the assessment question and response options remain unchanged.',
            ],
        };
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'missing') {
        return {
            kind: 'warn',
            title: 'External data item — country data unavailable',
            body: [
                'This item originates from an established external source. However, no country result was available from the original source, and no sufficiently reliable public evidence was identified through CPC Index research.',
                'The current score was therefore estimated using the applicable regional reference value. The confidence level records that the score is based on estimated rather than country-specific evidence.',
                'To preserve comparability with the original source, the assessment question and response options remain unchanged.',
            ],
        };
    }
    if (it.src === 'ExternalFixed' && it.dataFrom !== 'missing') {
        return {
            kind: 'info',
            title: 'External statistical item',
            body: [
                'The score is calculated directly from the country value reported by the external source. No government survey response is required.',
                'Where a more recent or corrected official country value is available, select Propose Revision and provide the supporting source.',
            ],
        };
    }
    if (it.src === 'ExternalFixed' && it.dataFrom === 'missing') {
        return {
            kind: 'warn',
            title: 'External statistical item — country data unavailable',
            body: [
                'The external source does not provide a country value for this item. The current score was therefore estimated using the applicable regional reference value. The confidence level records that the score is based on estimated rather than country-specific data.',
                'No government survey response is required. Where an official country value is available, select Propose Revision and provide the supporting source.',
            ],
        };
    }
    return null;
};

/** Rationale heading — overridden for the "updated externally-sourced" case. */
export const rationaleHeading = (it) => (
    it.src === 'ExternalSurvey' && it.dataFrom === 'internal'
        ? 'Updated supporting evidence — assessment rationale'
        : 'Assessment rationale'
);

/** {k,v} source-attribution lines shown under an item's evidence block. */
export const sourceLines = (it) => {
    if (it.src === 'Internal' && it.dataFrom === 'missing') {
        return [{k: 'Estimation basis:', v: it.region}];
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'external') {
        return [{k: 'Source:', v: `${it.extTitle}, ${it.extOrg}`}];
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'internal') {
        return [{k: 'Original external source:', v: `${it.extTitle}, ${it.extOrg}`}];
    }
    if (it.src === 'ExternalSurvey' && it.dataFrom === 'missing') {
        return [
            {k: 'Original external source:', v: `${it.extTitle}, ${it.extOrg}`},
            {k: 'Estimation basis:', v: it.region},
        ];
    }
    if (it.src === 'ExternalFixed' && it.dataFrom !== 'missing') {
        return [
            {k: 'Source:', v: `${it.extTitle}, ${it.extOrg}`},
            {k: 'Reference year:', v: it.year},
        ];
    }
    if (it.src === 'ExternalFixed' && it.dataFrom === 'missing') {
        return [
            {k: 'Source:', v: `${it.extTitle}, ${it.extOrg}`},
            {k: 'Estimation basis:', v: it.region},
        ];
    }
    return [];
};

/** Whether every item of an indicator has a recorded decision. */
export const indReviewed = (ind, reviews) => ind.items.every((it) => !!reviews?.[ind.code]?.[it.code]?.decision);
export const indAnyProposed = (ind, reviews) => ind.items.some((it) => reviews?.[ind.code]?.[it.code]?.decision === 'propose');
export const indReviewState = (ind, reviews) => (
    !indReviewed(ind, reviews) ? 'none' : indAnyProposed(ind, reviews) ? 'proposed' : 'agreed'
);

/**
 * Field-format validators for the Propose Revision form.
 *
 * All are permissive on an empty string — presence is `revisionComplete`'s
 * job, format is this file's. That split lets the page show a live format
 * error the moment a value looks wrong, without also nagging an untouched
 * required field before the reviewer has typed anything into it.
 */
export const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
export const isValidNumeric = (v) => !v || /^-?\d+(\.\d+)?$/.test(String(v).trim());
export const isValidYear = (v) => !v || /^\d{4}$/.test(String(v).trim());
export const isValidUrl = (v) => !v || /^https?:\/\/\S+\.\S+/i.test(String(v).trim());

/** Whether a proposed-revision form object has every required field filled in, in a valid format. */
export const revisionMissingFields = (rev) => {
    if (!rev) return [];
    const missing = [];

    if (!rev.reasons || rev.reasons.length === 0) missing.push('reasons');
    if (rev.reasons?.includes('other') && !rev.otherReason) missing.push('otherReason');

    if (!rev.noScoreChange) {
        if (rev.itemKind === 'value') {
            if (!rev.propValue || !isValidNumeric(rev.propValue)) missing.push('propValue');
            if (!rev.propUnit) missing.push('propUnit');
            if (!rev.propYear || !isValidYear(rev.propYear)) missing.push('propYear');
        } else if (!rev.proposedRubric) {
            missing.push('proposedRubric');
        }
    }

    ['evTitle', 'evIssuer', 'evDate', 'evJurisdiction', 'evLocation'].forEach((k) => {
        if (!rev[k]) missing.push(k);
    });
    if (!isValidUrl(rev.evUrl)) missing.push('evUrl');

    ['pName', 'pOrg', 'pDept', 'pRole', 'pEmail'].forEach((k) => {
        if (!rev[k]) missing.push(k);
    });
    if (rev.pEmail && !isValidEmail(rev.pEmail)) missing.push('pEmail');

    return missing;
};

/** Whether a proposed-revision form object has every required field filled in, in a valid format. */
export const revisionComplete = (rev) => !!rev && revisionMissingFields(rev).length === 0;
